import React, { Fragment, Component } from 'react';
import { Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as assessmentsActions from '../../redux/actions/assessmentsActions';
import * as sectionsActions from '../../redux/actions/sectionsActions';

import PlusMultIcon from '../icons/PlusMultIcon';
import InputErrors from '../common/InputErrors';

class NewAssessmentPrefacePage extends Component {
  constructor(props) {
    super(props);
    this.state = { nameError: false };
  }

  componentDidMount() {
    const { status, dispatchAssessmentsActions, dispatchSectionsActions, assessments, match, currentAssessment } = this.props;

    if (!status.sections) {
      dispatchSectionsActions.getSections();
    }
    if (!assessments[match.params.assessmentId]) {
      // grab the assessment from the database if necessary
      dispatchAssessmentsActions.getAssessment(match.params.assessmentId, true);
    } else if (currentAssessment.id !== match.params.assessmentId) {
      // no need for more from the database, just update the current assessment
      dispatchAssessmentsActions.updateCurrentAssessment(assessments[match.params.assessmentId]);
    }
  }

  componentDidUpdate(prevProps) {
    const { currentAssessment } = this.props;
    // If we added a participant or facilitator, focus the latest name input so they can type
    if (currentAssessment.facilitators.length > prevProps.currentAssessment.facilitators.length) {
      const nameInputs = document.querySelectorAll('.new-assessment-preface__name-input[data-category=facilitators]');
      nameInputs[nameInputs.length - 1].focus();
    } else if (currentAssessment.participants.length > prevProps.currentAssessment.participants.length) {
      const nameInputs = document.querySelectorAll('.new-assessment-preface__name-input[data-category=participants]');
      nameInputs[nameInputs.length - 1].focus();
    }
  }

  handleChange = (e) => {
    const value = e.target.value;
    const name = e.target.name;
    if (name === 'department' || name === 'area') {
      this.props.dispatchAssessmentsActions.saveAssessmentInfo({
        [name]: value
      });
    } else {
      // for participants and facilitators, save back the same array with just the one field changed
      const category = e.target.getAttribute('data-category');
      const index = e.target.getAttribute('data-index');
      const newArr = [...this.props.currentAssessment[category]];
      newArr[index][name] = value;
      this.props.dispatchAssessmentsActions.saveAssessmentInfo({
        [category]: newArr
      });
    }
  }

  addFacilitator = () => {
    this.props.dispatchAssessmentsActions.saveAssessmentInfo({
      facilitators: [
        ...this.props.currentAssessment.facilitators,
        {
          name: '',
          title: '',
        },
      ],
    });
  }

  addParticipant = () => {
    this.props.dispatchAssessmentsActions.saveAssessmentInfo({
      participants: [
        ...this.props.currentAssessment.participants,
        {
          name: '',
          title: '',
        },
      ],
    });
  }

  removeFacilitator = (index) => {
    let facilitators = [...this.props.currentAssessment.facilitators];
    facilitators.splice(index, 1);
    this.props.dispatchAssessmentsActions.saveAssessmentInfo({facilitators});
  }

  removeParticipant = (index) => {
    let participants = [...this.props.currentAssessment.participants];
    participants.splice(index, 1);
    this.props.dispatchAssessmentsActions.saveAssessmentInfo({participants});
  }

  handleSubmit = (e) => {
    this.setState({ nameError: false });
    let nameInputs = document.querySelectorAll('.new-assessment-preface__name-input');
    let hasErrors = [...nameInputs].some(input => !input.checkValidity());
    if (hasErrors) {
      // Prevent PUT assessment
      this.setState({ nameError: true });
    } else {
      // PUT and navigate to the first question
      this.props.dispatchAssessmentsActions.putAssessment(this.props.currentAssessment);
      const destinationUri = this.section.hasIntro ? '/intro' : '/1';
      this.props.history.push('/assessments/' + this.props.currentAssessment.id + destinationUri);
    }
  }

  render() {
    if (!this.props.currentAssessment.section) return <Redirect to="/assessments/new" />;
    this.section = this.props.sections[this.props.currentAssessment.section];
    if (!this.section) return null;

    // Aggregate error messages
    let errors = [];
    if (this.state.nameError) {
      errors = [...errors, 'Names may not be left empty.'];
    }

    return (
      <Fragment>
        <Helmet>
          <title>New Assessment</title>
        </Helmet>
        <div className="new-assessment-detail new-assessment-detail--no-sticky-buttons">
          <div className="row">
            <div className={'new-assessment-detail__heading color--' + this.section.theme}>
              {this.section.fullTitle}
            </div>
          </div>
          <div className="row">
            <div className="new-assessment-preface">
              <div className="new-assessment-preface__intro">
                <div className="new-assessment-preface__heading">
                  Purpose
                </div>
                <div className="new-assessment-preface__subheading">
                  {this.section.preface.split('\n\n').map((p, i) => <p key={i}>{p}</p>)}
                </div>
                <div className="new-assessment-preface__running-head">
                  Action Plan
                </div>
                <div className="new-assessment-preface__description">
                  With the information gathered from the tracer, what risk areas and opportunities are identified?
                </div>
              </div>
              <div className="form new-assessment-preface__form">
                {errors.length !== 0 && <InputErrors errors={errors} />}
                {this.section.hasArea && (
                  <div>
                    <label htmlFor="input-area">
                      Select the area for the {this.section.fullTitle} activity:
                    </label>
                    <input
                      id="input-area"
                      name="area"
                      value={this.props.currentAssessment.area || ''}
                      onChange={this.handleChange}
                      type="text"
                    />
                    {this.section.areaIntroText &&
                      this.section.areaIntroText.split('\n\n').map((pText, i) => (
                        // Allow for multi-paragraph text here by splitting on a double linebreak
                        <p className="new-assessment-preface__meta" key={i}>
                          {pText}
                        </p>
                      ))
                    }
                  </div>
                )}
                <label htmlFor="department">
                  Department:
                </label>
                <input
                  type="text"
                  name="department"
                  id="department"
                  value={this.props.currentAssessment.department || ''}
                  onChange={this.handleChange}
                />

                <label htmlFor="facilitator-name-1">
                  Facilitator:
                </label>
                {this.props.currentAssessment.facilitators.map((item, i) => (
                  <form key={i} className="new-assessment-preface__half-inputs-wrapper">
                    <input
                      type="text"
                      name="name"
                      id={`facilitator-name-${i + 1}`}
                      placeholder="Name"
                      data-category="facilitators"
                      data-index={i}
                      value={item.name || ''}
                      onChange={this.handleChange}
                      required
                      className="new-assessment-preface__name-input"
                    />
                    <input
                      type="text"
                      name="title"
                      id={`facilitator-title-${i + 1}`}
                      placeholder="Title"
                      data-category="facilitators"
                      data-index={i}
                      value={item.title || ''}
                      onChange={this.handleChange}
                    />
                    <button
                      type="button"
                      className="new-assessment-preface__remove-button"
                      onClick={() => this.removeFacilitator(i)}
                    >
                      <PlusMultIcon option="small" rotated />
                    </button>
                    </form>
                ))}
                <button
                  type="button"
                  onClick={this.addFacilitator}
                  className="new-assessment-preface__add-button"
                >
                  <PlusMultIcon option="small" />
                  Add Facilitator
                </button>

                <label htmlFor="participant-name-1">
                  Participant:*
                </label>
                {this.props.currentAssessment.participants.map((item, i) => (
                  <form key={i} className="new-assessment-preface__half-inputs-wrapper">
                    <input
                      type="text"
                      name="name"
                      id={`participant-name-${i + 1}`}
                      placeholder="Name"
                      data-category="participants"
                      data-index={i}
                      value={item.name || ''}
                      onChange={this.handleChange}
                      required
                      className="new-assessment-preface__name-input"
                    />
                    <input
                      type="text"
                      name="title"
                      id={`participant-title-${i + 1}`}
                      placeholder="Title"
                      data-category="participants"
                      data-index={i}
                      value={item.title || ''}
                      onChange={this.handleChange}
                    />
                    <button
                      type="button"
                      className="new-assessment-preface__remove-button"
                      onClick={() => this.removeParticipant(i)}
                    >
                      <PlusMultIcon option="small" rotated />
                    </button>
                  </form>
                ))}
                <button
                  type="button"
                  onClick={this.addParticipant}
                  className="new-assessment-preface__add-button"
                >
                  <PlusMultIcon option="small" />
                  Add Participant
                </button>
                <div className="new-assessment-preface__meta">
                  *Because multiple participants will be involved in contributing to the assessment, the interviewer may wish to note participants’ initials next to their responses within the module to help track the collective feedback.
                </div>
              </div>
              <div className="new-assessment-preface__button-wrapper">
                <button
                  // to={this.props.currentAssessment.id
                    // ? '/assessments/' + this.props.currentAssessment.id + '/1'
                    // : '/assessments/new/1'}
                  type="button"
                  className="button button--large"
                  onClick={this.handleSubmit}
                >
                  Begin Assessment
                </button>
              </div>
              <div className="new-assessment-preface__meta-wrapper">
                <div className="new-assessment-preface__meta">
                  As you progress through an assessment, your responses are automatically saved each time you advance to the next question. You can exit an assessment survey at any time to complete at a later date. The assessment will keep its “In Progress” status until you advance to the final question and click Submit Assessment.
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

function mapStateToProps(state) {
  return {
    sections: state.sections,
    assessments: state.assessments.list,
    currentAssessment: state.assessments.currentAssessment,
    status: state.status,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatchAssessmentsActions: bindActionCreators(assessmentsActions, dispatch),
    dispatchSectionsActions: bindActionCreators(sectionsActions, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(NewAssessmentPrefacePage);



// WEBPACK FOOTER //
// ./src/components/pages/NewAssessmentPrefacePage.js